import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Clock3, Github, GitBranch, Globe, Star } from "lucide-react";

type ProjectLink = {
    label: string;
    href: string;
    kind: "repo" | "live";
};

type FeaturedProject = {
    id: string;
    name: string;
    category: string;
    year: string;
    summary: string;
    description: string;
    highlights: string[];
    stack: string[];
    status: string;
    updatedAt: string;
    stars: number;
    forks: number;
    colors: [string, string];
    links: ProjectLink[];
};

type SatelliteProject = {
    name: string;
    summary: string;
    language: string;
    href: string;
};

const featuredProjects: FeaturedProject[] = [
    {
        id: "kontrakt",
        name: "Kontrakt++",
        category: "Compiler Contstruction / API Testing",
        year: "2025",
        summary: "A Domain Specific Language (DSL) and compiler",
        description:
            "Built with JFlex, CUP, AST construction, and code generation. You write readable \".test\" files, the program compiles it into actual JUnit tests against a Spring Boot backend.",
        highlights: ["Grammar and parsing", "AST pipeline", "Real test generation"],
        stack: ["C", "Java", "JFlex", "CUP", "JUnit", "Spring Boot"],
        status: "Added a new feature to compile the program to an OpenAPI yaml.",
        updatedAt: "2025-11-18T11:37:15Z",
        stars: 1,
        forks: 0,
        colors: ["#ff5c7a", "#f6c177"],
        links: [
            { label: "Repo", href: "https://github.com/notdulain/Kontrakt", kind: "repo" }
        ]
    },
    {
        id: "wiber",
        name: "Wiber",
        category: "Distributed Systems",
        year: "2025",
        summary: "A distributed messaging system with node control, live logs, and enough failure handling to keep it honest.",
        description:
            "This started as a Distrubuted Systems project and ended up way too complex to stay in that box. Multi-node lifecycle controls, a FastAPI gateway, real-time log streaming, message history, failover and RAFT consensus headaches, all running locally without pretending Kafka was the answer to every problem.",
        highlights: ["Cluster controls", "Gateway layer", "Live log streaming"],
        stack: ["Python", "FastAPI", "WebSockets", "Raft", "Fault tolerance", "HTML"],
        status: "Messy distributed-systems energy, but in a good way.",
        updatedAt: "2025-10-21T08:10:05Z",
        stars: 2,
        forks: 2,
        colors: ["#ff5c7a", "#8ec5ff"],
        links: [
            { label: "Repo", href: "https://github.com/notdulain/wiber", kind: "repo" }
        ]
    },
    {
        id: "meridian",
        name: "Meridian",
        category: "Microservices / Logistics Platform",
        year: "2026",
        summary: "A logistics platform split across a frontend, a gateway, and a small army of .NET microservices.",
        description:
            "This is the most \"okay fine, we are building an actual platform now\" project in the set. Deliveries, drivers, vehicles, live tracking, JWT auth, gRPC communication, Redis caching and the usual microservices ceremony, because apparently one repo was just too peaceful.",
        highlights: ["Microservices", "Azure Deployment", "Live tracking"],
        stack: ["Next.js", "TypeScript", ".NET 10", "ASP.NET Core", "SignalR", "Redis", "Azure"],
        status: "Still expanding, because microservices never really know when to stop.",
        updatedAt: "2026-03-17T17:46:54Z",
        stars: 2,
        forks: 0,
        colors: ["#ff5c7a", "#7ee7c8"],
        links: [
            { label: "Frontend", href: "https://github.com/notdulain/Meridian-Frontend", kind: "repo" },
            { label: "Backend", href: "https://github.com/notdulain/Meridian-Backend", kind: "repo" }
        ]
    },
    {
        id: "tmpl",
        name: "TMPL Website",
        category: "Live Event Scoring",
        year: "2025",
        summary: "Built this so live scoring would depend less on chaos and vibes alone.",
        description:
            "This one mattered because people were actually going to use it live, which is a great way to discover whether your UI decisions were smart or just confident. It had to be readable, fast, and reliable in the best possible way.",
        highlights: ["Score entry", "Live event use", "Firebase"],
        stack: ["TypeScript", "React", "CSS", "Vercel", "Firebase"],
        status: "Shipped, deployed, and useful under pressure. (Firebase is down tho 😢",
        updatedAt: "2025-05-10T12:44:33Z",
        stars: 0,
        forks: 1,
        colors: ["#ff5c7a", "#f1f0b9"],
        links: [
            { label: "Repo", href: "https://github.com/notdulain/tmpl-website", kind: "repo" },
            { label: "Live", href: "https://watchtmpl.com", kind: "live" }
        ]
    },
    {
        id: "algotimer",
        name: "AlgoTimer",
        category: "Utility / Interface Design",
        year: "2025",
        summary: "A giant countdown timer because opening the default stopwatch on stage felt criminal.",
        description:
            "Small project, yes. Throwaway project, no. It does one thing, does it cleanly, looks good on a projector, remembers state, and avoids the usual trap of adding features nobody asked for just because the codebase was feeling lonely.",
        highlights: ["Big-screen UI", "State persistence", "Fast build"],
        stack: ["Next.js", "TypeScript", "shadcn/ui", "CSS"],
        status: "Simple, useful, and not trying too hard.",
        updatedAt: "2025-09-02T07:05:34Z",
        stars: 0,
        forks: 0,
        colors: ["#ff5c7a", "#c6b8ff"],
        links: [
            { label: "Repo", href: "https://github.com/notdulain/algoTimer", kind: "repo" },
            { label: "Live", href: "https://algo-timer.vercel.app", kind: "live" }
        ]
    },
    {
        id: "naval",
        name: "Naval Battle Simulator",
        category: "Simulation / Systems Thinking",
        year: "2025",
        summary: "A first-year assignment that got promoted way past what the assignment asked for.",
        description:
            "Configurable grids, ship classes, probabilistic combat, movement paths, reload logic, and text telemetry everywhere. Basically, I took a perfectly normal assignment and kept pulling the thread until it turned into a full simulation sandbox.",
        highlights: ["Combat simulation", "Tactical logic", "Verbose telemetry"],
        stack: ["C", "Simulation", "CLI", "Report writing"],
        status: "Started as coursework, then refused to stay in its lane.",
        updatedAt: "2025-10-30T14:15:41Z",
        stars: 0,
        forks: 0,
        colors: ["#ff5c7a", "#78b7ff"],
        links: [
            { label: "Repo", href: "https://github.com/notdulain/Naval-Battle-Simulator", kind: "repo" }
        ]
    }
];

const satelliteProjects: SatelliteProject[] = [
    {
        name: "OAuth-Learning",
        summary: "A playground for OAuth 2.0, OpenID Connect, JWTs, and the joy of auth-related confusion.",
        language: "JavaScript",
        href: "https://github.com/notdulain/OAuth-Learning"
    },
    {
        name: "Hextractor",
        summary: "Tiny desktop utility for grabbing the color under the cursor without opening five design tools first.",
        language: "AutoHotkey",
        href: "https://github.com/notdulain/Hextractor"
    },
    {
        name: "RawRiser",
        summary: "Quick RAW-to-JPEG conversion tool for when the usual workflow starts feeling dramatic.",
        language: "HTML",
        href: "https://github.com/notdulain/RawRiser"
    },
    {
        name: "TextForge",
        summary: "A text-formatting app built to do the boring cleanup work so I would not have to.",
        language: "Python",
        href: "https://github.com/notdulain/TextForge"
    }
];

const formatMonthYear = (value: string) =>
    new Intl.DateTimeFormat("en", { month: "short", year: "numeric" }).format(new Date(value));

const linkIcon = (kind: ProjectLink["kind"]) =>
    kind === "live" ? <Globe className="w-4 h-4" /> : <Github className="w-4 h-4" />;

export default function ProjectsSection() {
    const [activeProjectId, setActiveProjectId] = useState(featuredProjects[0]?.id ?? "kontrakt");
    const activeProject = useMemo(
        () => featuredProjects.find((project) => project.id === activeProjectId) ?? featuredProjects[0],
        [activeProjectId]
    );

    return (
        <section id="projects" className="relative overflow-hidden border-t border-white/10 bg-[#070707] py-32">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,92,122,0.05),transparent_28%)]" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
                <div
                    className="h-full w-full"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)",
                        backgroundSize: "96px 96px"
                    }}
                />
            </div>

            <div className="relative mx-auto grid max-w-[1900px] grid-cols-1 gap-12 px-6 md:px-12 lg:grid-cols-12">
                <div className="lg:col-span-4">
                    <div className="sticky top-28 space-y-8">
                        <span className="block text-xs font-mono uppercase tracking-[0.25em] text-[#ff5c7a]">
                            04 / Build Log
                        </span>

                        <div className="space-y-5">
                            <h2 className="font-display text-4xl font-bold tracking-[-0.05em] text-white sm:text-5xl md:text-7xl whitespace-nowrap">
                                PROJECTS
                            </h2>
                            <p className="max-w-md text-lg leading-relaxed text-white/62 md:text-xl">
                                This is the part where I make it look like everything here happened casually. It didn't. Some are
                                tools, some are assignments and most just didn't end when they were supposed to ;)
                            </p>
                        </div>

                        <div className="space-y-3 border-l border-white/10 pl-5">
                            <div className="flex items-center justify-between gap-4 text-sm text-white/55">
                                <span className="font-mono uppercase tracking-[0.18em] text-white/35">Public Repos</span>
                                <span className="text-white">28</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm text-white/55">
                                <span className="font-mono uppercase tracking-[0.18em] text-white/35">On This Wall</span>
                                <span className="text-white">{featuredProjects.length}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm text-white/55">
                                <span className="font-mono uppercase tracking-[0.18em] text-white/35">Years I Was Busy</span>
                                <span className="text-white">2003 - Present</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-6 lg:col-span-8">
                    <div className="grid auto-rows-fr grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {featuredProjects.map((project, index) => {
                            const isActive = project.id === activeProject.id;

                            return (
                                <button
                                    key={project.id}
                                    type="button"
                                    onClick={() => setActiveProjectId(project.id)}
                                    aria-pressed={isActive}
                                    className={`group flex h-full min-h-[15rem] flex-col justify-between border p-6 text-left transition-all duration-300 ${isActive
                                        ? "border-[#ff5c7a]/35 bg-[#111111]"
                                        : "border-white/10 bg-[#0c0c0c] hover:border-white/20 hover:bg-[#101010]"
                                        }`}
                                >
                                    <div className="space-y-5">
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
                                                0{index + 1}
                                            </span>
                                            <span
                                                className="h-2 w-2 rounded-full"
                                                style={{ backgroundColor: isActive ? project.colors[0] : "rgba(255,255,255,0.24)" }}
                                            />
                                        </div>

                                        <div>
                                            <h3 className="text-2xl font-display text-white">{project.name}</h3>
                                            <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
                                                {project.category}
                                            </p>
                                        </div>

                                        <p className="text-base leading-relaxed text-white/62">{project.summary}</p>
                                    </div>

                                    <div className="mt-8 flex items-center justify-between gap-4 border-t border-white/10 pt-4 text-sm text-white/48">
                                        <span>{project.year}</span>
                                        <span>{formatMonthYear(project.updatedAt)}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeProject.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden border border-white/10 bg-[#0f0f0f]"
                        >
                            <div
                                className="h-px w-full"
                                style={{
                                    background: `linear-gradient(90deg, transparent 0%, ${activeProject.colors[0]} 50%, transparent 100%)`
                                }}
                            />

                            <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr]">
                                <div className="border-b border-white/10 p-8 md:p-10 xl:border-b-0 xl:border-r">
                                    <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-white/38">
                                        <span>Picked one</span>
                                        <span className="h-px w-8 bg-white/12" />
                                        <span>{activeProject.category}</span>
                                        <span className="h-px w-8 bg-white/12" />
                                        <span>{activeProject.year}</span>
                                    </div>

                                    <div className="mt-8 space-y-6">
                                        <div>
                                            <h3 className="text-4xl font-display tracking-[-0.04em] text-white md:text-6xl">
                                                {activeProject.name}
                                            </h3>
                                            <p className="mt-4 max-w-3xl text-xl leading-relaxed text-white/78 md:text-2xl">
                                                {activeProject.summary}
                                            </p>
                                        </div>

                                        <p className="max-w-3xl text-lg leading-relaxed text-white/60">
                                            {activeProject.description}
                                        </p>
                                    </div>

                                    <div className="mt-8 flex flex-wrap gap-3">
                                        {activeProject.links.map((link) => (
                                            <a
                                                key={link.href}
                                                href={link.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-3 border border-white/20 bg-white/[0.03] px-5 py-3 text-sm uppercase tracking-[0.18em] text-white transition-all duration-300 hover:bg-white hover:text-black"
                                            >
                                                {linkIcon(link.kind)}
                                                <span>{link.label}</span>
                                                <ArrowUpRight className="h-4 w-4" />
                                            </a>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-8 bg-[#0b0b0b] p-8 md:p-10">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="border border-white/10 bg-black/20 p-4">
                                            <div className="flex items-center gap-2 text-white/38">
                                                <Clock3 className="h-4 w-4" />
                                                <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Last touched</span>
                                            </div>
                                            <p className="mt-3 text-sm text-white/78">{formatMonthYear(activeProject.updatedAt)}</p>
                                        </div>
                                        <div className="border border-white/10 bg-black/20 p-4">
                                            <div className="flex items-center gap-2 text-white/38">
                                                <Star className="h-4 w-4" />
                                                <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Stars</span>
                                            </div>
                                            <p className="mt-3 text-sm text-white/78">{activeProject.stars}</p>
                                        </div>
                                        <div className="border border-white/10 bg-black/20 p-4">
                                            <div className="flex items-center gap-2 text-white/38">
                                                <GitBranch className="h-4 w-4" />
                                                <span className="text-[10px] font-mono uppercase tracking-[0.2em]">Forks</span>
                                            </div>
                                            <p className="mt-3 text-sm text-white/78">{activeProject.forks}</p>
                                        </div>
                                        <div className="border border-white/10 bg-black/20 p-4">
                                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/38">Current state</span>
                                            <p className="mt-3 text-sm leading-relaxed text-white/78">{activeProject.status}</p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <span className="block text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
                                            Worth noticing
                                        </span>
                                        <div className="space-y-3">
                                            {activeProject.highlights.map((highlight) => (
                                                <div key={highlight} className="border border-white/10 bg-black/20 px-4 py-3 text-sm text-white/72">
                                                    {highlight}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <span className="block text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
                                            Stack / usual suspects
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            {activeProject.stack.map((item) => (
                                                <span
                                                    key={item}
                                                    className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm text-white/62"
                                                >
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="border border-white/10 bg-[#0b0b0b] p-6 md:p-8">
                        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                            <div>
                                <span className="block text-[11px] font-mono uppercase tracking-[0.22em] text-white/35">
                                    Other oddities
                                </span>
                                <p className="mt-3 max-w-2xl text-base leading-relaxed text-white/55">
                                    Smaller builds, narrower scopes, same disease. They still say something useful about
                                    what I like making.
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                            {satelliteProjects.map((project) => (
                                <div
                                    key={project.name}
                                    className="flex flex-col gap-4 border border-white/10 bg-black/20 p-5 md:flex-row md:items-start md:justify-between"
                                >
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3">
                                            <h4 className="text-xl font-display text-white">{project.name}</h4>
                                            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/35">
                                                {project.language}
                                            </span>
                                        </div>
                                        <p className="text-sm leading-relaxed text-white/58">{project.summary}</p>
                                    </div>

                                    <a
                                        href={project.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 self-start border border-white/15 px-3 py-2 text-[11px] font-mono uppercase tracking-[0.18em] text-white/72 transition-colors duration-300 hover:bg-white hover:text-black"
                                    >
                                        <span>Inspect</span>
                                        <ArrowUpRight className="h-3.5 w-3.5" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
